import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Calendar, Clock, DollarSign, Check, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function AcademyEnrollment({ user }) {
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [discountRules, setDiscountRules] = useState([]);
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isParent = user?.roles?.includes('academy_parent');

  useEffect(() => {
    if (isParent) {
      fetchActiveTerms();
      fetchChildren();
      fetchDiscountRules();
    }
  }, [isParent]);

  useEffect(() => {
    if (selectedTerm) {
      fetchSessions(selectedTerm.id);
    }
  }, [selectedTerm]);

  useEffect(() => {
    calculateFees();
  }, [selectedSessions, sessions, discountRules]);

  const fetchActiveTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      const activeTerms = response.data.filter(t => t.status === 'active');
      setTerms(activeTerms);
      if (activeTerms.length > 0) {
        setSelectedTerm(activeTerms[0]);
      }
    } catch (error) {
      console.error("Error fetching terms:", error);
      toast.error("Failed to load terms");
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async (termId) => {
    try {
      const response = await axios.get(`${API}/academy/terms/${termId}/sessions`, { withCredentials: true });
      // Group sessions by day
      const sessionsByDay = response.data.reduce((acc, session) => {
        if (!acc[session.day_of_week]) {
          acc[session.day_of_week] = [];
        }
        acc[session.day_of_week].push(session);
        return acc;
      }, {});
      setSessions(sessionsByDay);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    }
  };

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/academy/my-kids`, { withCredentials: true });
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching children:", error);
      toast.error("Failed to load children");
    }
  };

  const fetchDiscountRules = async () => {
    try {
      const response = await axios.get(`${API}/academy/discount-rules`, { withCredentials: true });
      // Sort by session count
      const sorted = response.data.sort((a, b) => a.session_count - b.session_count);
      setDiscountRules(sorted);
    } catch (error) {
      console.error("Error fetching discount rules:", error);
    }
  };

  const toggleSession = (session) => {
    const isSelected = selectedSessions.find(s => s.id === session.id);
    if (isSelected) {
      setSelectedSessions(selectedSessions.filter(s => s.id !== session.id));
    } else {
      setSelectedSessions([...selectedSessions, session]);
    }
  };

  const calculateFees = () => {
    if (selectedSessions.length === 0 || !selectedTerm) {
      setCalculatedTotal(0);
      setBreakdown([]);
      return;
    }

    const sortedSessions = [...selectedSessions].sort((a, b) => b.price - a.price);
    let total = 0;
    const itemBreakdown = [];

    sortedSessions.forEach((session, index) => {
      const sessionCount = index + 1;
      const basePrice = session.price * selectedTerm.duration_weeks;
      
      // Find applicable discount
      const applicableDiscount = discountRules
        .filter(rule => rule.session_count === sessionCount)
        .pop();

      let discountAmount = 0;
      let finalPrice = basePrice;

      if (applicableDiscount) {
        if (applicableDiscount.discount_type === 'percentage') {
          discountAmount = (basePrice * applicableDiscount.discount_value) / 100;
        } else {
          discountAmount = applicableDiscount.discount_value * selectedTerm.duration_weeks;
        }
        finalPrice = basePrice - discountAmount;
      }

      total += finalPrice;

      itemBreakdown.push({
        session,
        sessionCount,
        basePrice,
        discount: applicableDiscount,
        discountAmount,
        finalPrice
      });
    });

    setCalculatedTotal(total);
    setBreakdown(itemBreakdown);
  };

  const proceedToPayment = () => {
    if (!selectedChild) {
      toast.error("Please select a child");
      return;
    }
    if (selectedSessions.length === 0) {
      toast.error("Please select at least one session");
      return;
    }
    setShowSummary(true);
  };

  const confirmEnrollment = async () => {
    setSubmitting(true);
    try {
      const enrollmentData = {
        term_id: selectedTerm.id,
        player_id: selectedChild.id,
        selected_sessions: selectedSessions.map(s => s.id),
        total_amount: calculatedTotal,
        breakdown: breakdown
      };

      const response = await axios.post(
        `${API}/academy/enrollments`,
        enrollmentData,
        { withCredentials: true }
      );

      toast.success("Enrollment created! Proceeding to payment...");
      navigate(`/academy/enrollment/${response.data.enrollment_id}/payment`);
    } catch (error) {
      console.error("Error creating enrollment:", error);
      toast.error(error.response?.data?.detail || "Failed to create enrollment");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isParent) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-4">
        <div className="max-w-2xl mx-auto text-center py-16">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-slate-400">Only parents can enroll children in sessions.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (terms.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-4">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Calendar className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">No Active Terms</h1>
          <p className="text-slate-400">There are no active enrollment periods at the moment.</p>
          <p className="text-slate-400 mt-2">Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/academy/my-kids">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">Session Enrollment</h1>
              <p className="text-slate-400">{selectedTerm?.name}</p>
            </div>
          </div>
        </div>

        {/* Child Selection */}
        {children.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Select Child</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {children.map(child => (
                <div
                  key={child.id}
                  onClick={() => setSelectedChild(child)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedChild?.id === child.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-white/5 hover:border-blue-500/30'
                  }`}
                >
                  <p className="font-semibold text-white">{child.child_name}</p>
                  <p className="text-sm text-slate-400">{child.membership_number}</p>
                  {selectedChild?.id === child.id && (
                    <Check className="w-5 h-5 text-blue-400 mt-2" />
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Session Selection */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Select Sessions</h2>
          <p className="text-sm text-slate-400 mb-6">
            Choose which sessions your child will attend. Sessions run for {selectedTerm?.duration_weeks} weeks.
          </p>

          <div className="space-y-4">
            {Object.keys(sessions).sort((a, b) => a - b).map(dayOfWeek => (
              <div key={dayOfWeek}>
                <h3 className="font-semibold text-white mb-3 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-blue-400" />
                  {DAYS_OF_WEEK[dayOfWeek]}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-7">
                  {sessions[dayOfWeek].map(session => {
                    const isSelected = selectedSessions.find(s => s.id === session.id);
                    return (
                      <div
                        key={session.id}
                        onClick={() => toggleSession(session)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-green-500 bg-emerald-500/10'
                            : 'border-white/5 hover:border-emerald-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold px-2 py-1 rounded bg-blue-500/20 text-blue-400">
                                {session.session_type === 'training' ? '🎓 Training' : '⚽ Game'}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-slate-400 mb-1">
                              <Clock className="w-4 h-4 mr-1" />
                              {session.start_time} - {session.end_time}
                            </div>
                            <div className="flex items-center text-sm font-semibold text-white">
                              <DollarSign className="w-4 h-4 mr-1" />
                              ${session.price} per session
                            </div>
                            {session.description && (
                              <p className="text-xs text-slate-400 mt-2">{session.description}</p>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="w-6 h-6 text-emerald-400" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {Object.keys(sessions).length === 0 && (
              <p className="text-slate-400 text-center py-8">No sessions available for this term</p>
            )}
          </div>
        </Card>

        {/* Fee Summary */}
        {selectedSessions.length > 0 && (
          <Card className="p-6 mb-6 border-2 border-blue-500/30 bg-blue-500/10">
            <h2 className="text-xl font-bold text-white mb-4">Fee Calculation</h2>
            
            <div className="space-y-3 mb-4">
              {breakdown.map((item, index) => (
                <div key={item.session.id} className="bg-slate-900/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-white">
                        {item.sessionCount === 1 ? '1st' : 
                         item.sessionCount === 2 ? '2nd' :
                         item.sessionCount === 3 ? '3rd' :
                         `${item.sessionCount}th`} Session: {DAYS_OF_WEEK[item.session.day_of_week]} {item.session.start_time}
                      </p>
                      <p className="text-sm text-slate-400">
                        ${item.session.price} × {selectedTerm.duration_weeks} weeks
                      </p>
                    </div>
                    <p className="font-bold text-white">${item.basePrice.toFixed(2)}</p>
                  </div>
                  
                  {item.discount && (
                    <div className="flex items-center justify-between text-sm text-emerald-400 bg-emerald-500/20 px-3 py-2 rounded mt-2">
                      <span>
                        {item.discount.discount_type === 'percentage' 
                          ? `${item.discount.discount_value}% discount` 
                          : `$${item.discount.discount_value} discount per week`}
                      </span>
                      <span className="font-semibold">-${item.discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2 pt-2 border-t">
                    <span className="font-semibold text-white">Subtotal:</span>
                    <span className="font-bold text-blue-400">${item.finalPrice.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t-2 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-white">Total Amount:</span>
                <span className="text-3xl font-bold text-blue-400">${calculatedTotal.toFixed(2)}</span>
              </div>
              <p className="text-sm text-slate-400 mt-2">
                For {selectedTerm.duration_weeks} weeks ({selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''} per week)
              </p>
            </div>

            <Button 
              onClick={proceedToPayment}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 py-6 text-lg"
            >
              Proceed to Payment
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Card>
        )}

        {selectedSessions.length === 0 && (
          <Card className="p-6 text-center">
            <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-400">Select sessions above to see fee calculation</p>
          </Card>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={showSummary} onOpenChange={setShowSummary}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Confirm Enrollment</DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              <div className="bg-blue-500/10 p-4 rounded-lg mb-4">
                <p className="font-semibold text-white">Student: {selectedChild?.child_name}</p>
                <p className="text-sm text-slate-400">{selectedChild?.membership_number}</p>
                <p className="text-sm text-slate-400 mt-2">Term: {selectedTerm?.name}</p>
                <p className="text-sm text-slate-400">Duration: {selectedTerm?.duration_weeks} weeks</p>
              </div>

              <h3 className="font-semibold text-white mb-3">Selected Sessions:</h3>
              <div className="space-y-2 mb-4">
                {selectedSessions.map(session => (
                  <div key={session.id} className="flex items-center justify-between p-3 bg-[#0a0f1a] rounded">
                    <span className="text-sm">
                      {DAYS_OF_WEEK[session.day_of_week]} {session.start_time}-{session.end_time}
                    </span>
                    <span className="text-sm font-semibold">${session.price}/session</span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 mb-6">
                <div className="flex items-center justify-between text-xl font-bold">
                  <span>Total Amount:</span>
                  <span className="text-blue-400">${calculatedTotal.toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={confirmEnrollment}
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6"
                >
                  {submitting ? "Processing..." : "Confirm & Proceed to Payment"}
                </Button>
                <Button 
                  onClick={() => setShowSummary(false)}
                  variant="outline"
                  className="w-full"
                >
                  Go Back
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
